import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, User, Award, Search } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", address: "", nationality: "", id_type: "Passport", id_number: "", preferences: "" };

export default function Guests() {
    const { activeHotelId } = useAuth();
    const [guests, setGuests] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);

    const load = async () => {
        if (!activeHotelId) return;
        const url = q ? `/guests?hotel_id=${activeHotelId}&q=${encodeURIComponent(q)}` : `/guests?hotel_id=${activeHotelId}`;
        try { const { data } = await api.get(url); setGuests(data); } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { load(); }, [activeHotelId]);

    const submit = async () => {
        try {
            await api.post("/guests", { ...form, hotel_id: activeHotelId });
            toast.success("Guest created");
            setOpen(false); setForm(empty); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Guests" subtitle={`${guests.length} guest profiles`}>
            <div className="flex justify-between items-center mb-4 gap-2">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input placeholder="Search guests..." value={q} onChange={e => setQ(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && load()}
                        className="pl-9" data-testid="guest-search" />
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="add-guest-btn"><Plus className="w-4 h-4 mr-1" /> New Guest</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>New Guest</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            {[["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["nationality", "Nationality"], ["id_number", "ID Number"], ["preferences", "Preferences"]].map(([k, l]) => (
                                <div key={k}><Label className="text-xs uppercase tracking-wider">{l}</Label>
                                    <Input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`guest-${k}`} /></div>
                            ))}
                        </div>
                        <DialogFooter><Button onClick={submit} disabled={!form.name} className="bg-[#0A1C3E]" data-testid="guest-submit">Create</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {guests.map(g => (
                    <Card key={g.id} className="p-4 border border-slate-200 rounded-md shadow-none hover:border-slate-400" data-testid={`guest-${g.id}`}>
                        <div className="flex justify-between items-start">
                            <div className="w-10 h-10 rounded-full bg-[#0A1C3E] text-[#C5B08A] flex items-center justify-center font-semibold">
                                {g.name?.charAt(0).toUpperCase()}
                            </div>
                            {g.loyalty_points > 0 && (
                                <div className="text-xs flex items-center gap-1 text-[#C5B08A]"><Award className="w-3 h-3" /> {g.loyalty_points} pts</div>
                            )}
                        </div>
                        <div className="mt-3 font-semibold text-slate-900">{g.name}</div>
                        <div className="text-xs text-slate-500">{g.email || "—"}</div>
                        <div className="text-xs text-slate-500">{g.phone || "—"}</div>
                        <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">{g.nationality || "—"} · {g.id_type} {g.id_number ? "✓" : ""}</div>
                    </Card>
                ))}
                {guests.length === 0 && <div className="text-slate-500 text-sm col-span-full text-center py-10">No guests yet</div>}
            </div>
        </AppShell>
    );
}
